import React, { useEffect, useState } from "react";
import RacecardAndResultWidgetSimple from "./RacecardAndResultWidgetSimple";
import RacecardAndResultWidgetDetailed from "./RacecardAndResultWidgetDetailed";
import {
  RacecardRes,
  ResultRes,
} from "../../../../types/races/racecardAndResultWidget";
import { APIResponse } from "../../../../types/responses/APIResponse";
import { fetchGet } from "../../../../client";
import RacecardAndResultWidgetLoader from "../../../States/Loading/Loaders/cms/RacecardAndResultWidgetLoader";
import { View } from "react-native";

/**
 * RacecardAndResultWidget displays either a racecard or result view based on
 * the race status. It fetches data using the provided race ID and adjusts its
 * layout based on the container width
 *
 * For smaller container widths, a simpler design is displayed
 * For larger container widths, a detailed design is displayed with more information
 * on the runners and totes
 *
 * @prop {number} containerWidth - The width of the container to determine layout
 * @prop {string} id - The race ID to fetch data for
 *
 * @returns A React component rendering the racecard or result view
 *
 */

type Props = {
  containerWidth: number;
  id: string;
};

const DETAILED_VIEW_MIN_WIDTH = 870;

const RacecardAndResultWidget = ({ containerWidth, id }: Props) => {
  const [data, setData] = useState<RacecardRes | ResultRes>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const res: APIResponse<{ data: RacecardRes | ResultRes }> =
          await fetchGet(`/widgets/racecard-and-result-widget/${id}`);
        if (res.data) setData(res.data);
      } catch (err) {
        setError(
          "Error loading race: " +
            (err instanceof Error ? err.message : "Unknown")
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading)
    return (
      <View style={{ width: "100%" }}>
        <RacecardAndResultWidgetLoader />
      </View>
    );

  if (error || !data) return null;

  const isRacecard =
    data.status.state === "scheduled" || data.status.state === "started";

  return containerWidth > DETAILED_VIEW_MIN_WIDTH ? (
    <RacecardAndResultWidgetDetailed data={data} isRacecard={isRacecard} />
  ) : (
    <RacecardAndResultWidgetSimple
      data={data}
      isRacecard={data?.status?.state !== "resulted"}
    />
  );
};

export default RacecardAndResultWidget;
